import { Theme } from '@mui/material';
import { SxProps, alpha } from '@mui/system';
import {
  Color,
  TableColumnFragment,
  type TableRowColumn,
  type TableRowFragment,
  TableViewColumnFragment,
} from 'src/generated/graphql';
import { functionTypes } from '../BoardPage/TableViewPage/TableViewList/TableViewListRow/RowSummary/utils';
import {
  type TableViewColumnsOptionQueryResultItem,
  TableViewColumnsQueryResultItem,
} from './types';

export function getPositionForIndex(list: number[], index: number): number {
  if (list.length === 0) {
    return 65536;
  }

  if (index === 0) {
    if (list[index] === undefined) return 65536;

    const order = list[index] <= 0 ? list[index] - 65536 : list[index];
    return parseInt((order / 2).toFixed(), 10);
  }

  if (index === list.length) {
    return list[list.length - 1] + 65536;
  }

  const previewPosition = list[index - 1];
  const nestPosition = list[index];

  return parseInt(((previewPosition + nestPosition) / 2).toFixed(), 10);
}

export const getBoardColorStyle =
  (color: Color): SxProps<Theme> =>
  ({ palette: { userPreference, getContrastText } }) =>
    color && {
      background: userPreference[color],
      color: getContrastText(userPreference[color]),
      '&:hover': {
        background: alpha(userPreference[color], 0.8),
      },
    };

export const getRandomBoardColor = (): Color => {
  const colors = Object.values(Color);
  const randomIndex = Math.floor(Math.random() * colors.length);
  return colors[randomIndex];
};

export const getTableViewRecord = ({
  viewColumns,
  tableColumns,
}: {
  tableColumns: TableColumnFragment[];
  viewColumns: TableViewColumnFragment[];
}) => {
  const columnMap = viewColumns.reduce(
    (acc, column) => {
      acc[column.columnId] = column;
      return acc;
    },
    {} as Record<string, TableViewColumnFragment>,
  );

  let columns: TableViewColumnsQueryResultItem[] = [];
  let hiddenColumns: TableViewColumnsQueryResultItem[] = [];
  let otherTableColumns: TableColumnFragment[] = [];
  let visibleColumns: TableViewColumnsQueryResultItem[] = [];
  const columnsById: Record<string, TableViewColumnsQueryResultItem> = {};
  const columnByField: Record<string, TableViewColumnsQueryResultItem> = {};
  const columnOptionsById: Record<
    string,
    TableViewColumnsOptionQueryResultItem
  > = {};

  tableColumns.forEach((tableColumn) => {
    const viewColumn = columnMap[tableColumn._id];
    if (!viewColumn) {
      otherTableColumns.push(tableColumn);
      return;
    }

    const columnItem: TableViewColumnsQueryResultItem = {
      ...tableColumn,
      order: viewColumn.order,
      fixed: viewColumn.fixed,
      width: viewColumn.width,
      hidden: viewColumn.hidden,
    };

    if (viewColumn.hidden) {
      hiddenColumns.push(columnItem);
    } else {
      visibleColumns.push(columnItem);
    }

    columns.push(columnItem);
    columnsById[viewColumn.columnId] = columnItem;
    if (tableColumn.field) {
      columnByField[tableColumn.field] = columnItem;
    }
    if (tableColumn.options?.length)
      tableColumn.options.forEach((option) => {
        columnOptionsById[option._id] = option;
      });
  });

  otherTableColumns = otherTableColumns.sort((a, b) =>
    a.label.localeCompare(b.label),
  );
  columns = columns.sort((a, b) => a.order - b.order);
  hiddenColumns = hiddenColumns.sort((a, b) => a.order - b.order);
  visibleColumns = visibleColumns.sort((a, b) => a.order - b.order);

  return {
    columns,
    columnByField,
    columnsById,
    hiddenColumns,
    visibleColumns,
    otherTableColumns,
    columnOptionsById,
  };
};

export const normalizeMultColumnValue = (value: any): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

export const createTableColumnAccessorFn = <
  Row extends TableRowFragment,
  Column extends TableColumnFragment,
>(
  rowColumnsById: Record<string, Record<string, TableRowColumn>>,
  column: Column,
) => {
  return (row: Row) => {
    if (column.field === 'owner') return row.ownerId || 'Empty';

    const col = rowColumnsById[row._id][column._id];

    if (functionTypes[column.type]) return Number(col?.value || 0);

    if (!col) return 'Empty';

    const value = col.linkValue || col.value;

    const val = Array.isArray(value) ? value : [value];

    return val.length ? val : 'Empty';
  };
};
